import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'hooks';
import classNames from 'classnames';

import { useToast } from '~libs/toast';

import { Button } from '~components';
import Icon, { faPlus } from '~components/icon';
import FileOffCanvas from '~support-at-home/components/common/FileOffCanvas';

import { useGetCompanySettingsQuery } from '~main/actions/companySettings';

import { uploadInvoiceFile, useAnalyseUploadedFileMutation } from '~support-at-home/actions/supplierInvoices';

import { AnalyseInvoiceResponse, InvoiceFile } from '~support-at-home/models';

import SupplierInvoiceModal from './SupplierInvoiceModal';

interface ComponentProps {
  authTag?: string;
  load?: () => void;
}

const AddSupplierInvoiceButton: React.FC<ComponentProps> = ({ authTag, load }) => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isFileCanvasCollapsed, setIsFileCanvasCollapsed] = useState<boolean>(false);
  const [file, setFile] = useState<File | undefined>();
  const [ocrResult, setOcrResult] = useState<AnalyseInvoiceResponse | null>(null);
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const { data: companySettings = [] } = useGetCompanySettingsQuery();
  const ocrSetting = companySettings.find((setting) => setting.setting.key === 'ocr_invoice_enabled');
  const ocrEnabled = ocrSetting?.value === 'true';

  const [analyseFile, { isLoading: isAnalysing }] = useAnalyseUploadedFileMutation();

  const onClose = (confirm: boolean, id?: string) => {
    setModalOpen(false);

    if (confirm && file && id) {
      dispatch(uploadInvoiceFile({ file, id }));
    }
    setFile(undefined);
    setOcrResult(null);
    load?.();
  };

  const handleFileChange = (selectedFile: File) => {
    setFile(selectedFile);
    setOcrResult(null);
  };

  const handleAnalyse = async () => {
    if (!file) return;

    try {
      const result = await analyseFile({ file }).unwrap();
      setOcrResult(result);
      showToast('success', 'Invoice analysed — please review the extracted fields.');
    } catch {
      showToast('danger', 'Unable to analyse invoice. Please enter details manually.');
    }
  };

  // Manage blob URL lifecycle to prevent memory leaks
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setBlobUrl(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setBlobUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Create a synthetic file entry for FileOffCanvas display
  const previewFiles: InvoiceFile[] = useMemo(
    () =>
      file && blobUrl
        ? [
            {
              id: 'pending-upload',
              invoice_id: '',
              file_name: file.name,
              file_type: file.type,
              file_path: '',
              data: blobUrl,
            },
          ]
        : [],
    [file, blobUrl],
  );

  const modalClassName = classNames({
    'full-view': isFileCanvasCollapsed,
  });

  return (
    <>
      <Button onClick={() => setModalOpen(true)} size="sm" color="success" authTag={authTag}>
        <Icon className="me-2" icon={faPlus} />
        New Invoice
      </Button>
      <SupplierInvoiceModal
        className={modalClassName}
        isOpen={isModalOpen}
        onClose={onClose}
        isNew={true}
        authTag={authTag}
        ocrResult={ocrResult}
      />
      <FileOffCanvas
        isOpen={isModalOpen}
        onChange={handleFileChange}
        onToggleCollapse={setIsFileCanvasCollapsed}
        selectorFiles={previewFiles}
        onAnalyse={handleAnalyse}
        isAnalysing={isAnalysing}
        analyseEnabled={ocrEnabled}
      />
    </>
  );
};

export default AddSupplierInvoiceButton;
